// src/encounter/lib/to-medication-admin-requests.ts
// Pre-encounter MEDICATION ADMINISTRATION REQUESTS mapper (PROD-1524).
//
// Port of Flutter `medicationAdministrationRequest()` (pre_encounter_widget.dart:1595): the A/P
// medications whose `frequency == AppConstants.frequencyNow` ("now") — the "give it now" orders a
// nurse administers on arrival (each Flutter row raises Give / Decline / Edit). Flutter behaviour
// reproduced:
//   • FILTER to `frequency === 'now'` only (the medication sibling of the vaccination-requests
//     section — that one has no such filter; this one does).
//   • title = the medication display (`medicationCode.display`); detail = the sig/route line.
//   • the section is hidden when the filtered list is empty (Flutter `length > 0 ? Column : Container`).
//
// Deliberate divergence from Flutter (correctness, matches this repo's soft-delete convention):
// soft-deleted medications (isDeleted === true) are EXCLUDED. PURE — no I/O. The frequency compare
// reuses the real wire constant (MED_FREQUENCY.now) so it can never drift from the BE FrequencyEnum.
//
// PRESENTATIONAL host: this maps a MINIMAL, defined input shape (a subset of AssessmentPlanMedication) —
// the parent surface wires the real PatientAssessment / hook and passes the filtered medications in.
import { MED_FREQUENCY } from './medication-frequency';

/** The minimal medication fields this mapper reads (subset of wire AssessmentPlanMedication). */
export interface MedicationAdminRequestInput {
  identifier?: string | null;
  /** Wire `frequency` — only `'now'` (MED_FREQUENCY.now) rows become administration requests. */
  frequency?: string | null;
  /** Prescribed medication coding — `display` is the row title. */
  medicationCode?: { display?: string | null; code?: string | null } | null;
  /** Dosage instructions (Flutter sig line) — the row detail when present. */
  sig?: string | null;
  /** Route of administration coding — detail fallback when there's no sig. */
  route?: { display?: string | null } | null;
  /** When the administration was requested (no-Z ISO) or null. */
  createdDate?: string | null;
  /** Requesting clinician (loosely typed wire Record) — `name` is the requester. */
  performerDetails?: Record<string, unknown> | null;
  /** Soft-delete flag — deleted rows are excluded. */
  isDeleted?: boolean | null;
}

export interface MedicationAdminRequestView {
  /** Stable React key + handle for the administration action. First occurrence's id, or a fallback. */
  id: string;
  /** Medication display name (the Flutter row title). '' when the coding has no display. */
  title: string;
  /** Sig / route line shown under the title. Omitted when neither is present. */
  detail?: string;
  /** When the administration was requested (createdDate, no-Z ISO) or null. */
  requestedOn?: string | null;
  /** Requesting clinician's display name (performerDetails.name) or null. */
  requestedBy?: string | null;
}

/** Read `performerDetails.name` defensively (the wire field is loosely typed Record). */
function performerName(pd: Record<string, unknown> | null | undefined): string | null {
  const name = pd?.name;
  return typeof name === 'string' && name.trim() !== '' ? name : null;
}

/** The row detail: prefer the sig, else the route display; undefined when neither exists. */
function rowDetail(med: MedicationAdminRequestInput): string | undefined {
  const sig = med.sig?.trim();
  if (sig) return sig;
  const route = med.route?.display?.trim();
  return route || undefined;
}

export function toMedicationAdminRequests(
  medications: ReadonlyArray<MedicationAdminRequestInput> | null | undefined,
): MedicationAdminRequestView[] {
  if (!medications || medications.length === 0) return [];

  const out: MedicationAdminRequestView[] = [];
  // A med linked to N diagnoses is nested once under each (PROD-1428 fan-out), so the flattened input
  // holds N copies with DISTINCT server ids. Flutter feeds this list from apData.medications(), which
  // merges the copies by title, so show each med ONCE, keyed by code, else display.
  const seen = new Set<string>();
  for (const med of medications) {
    // Flutter parity: only `frequency == frequencyNow` medications are administration requests.
    if (med.frequency !== MED_FREQUENCY.now) continue;
    // DELIBERATE divergence — exclude soft-deleted rows (showing a deleted administration is a defect).
    if (med.isDeleted === true) continue;

    const title = med.medicationCode?.display ?? '';
    const key = med.medicationCode?.code?.trim() || title.trim();
    if (key) {
      if (seen.has(key)) continue;
      seen.add(key);
    }
    out.push({
      id: med.identifier ?? `med-${title}-${out.length}`,
      title,
      detail: rowDetail(med),
      requestedOn: med.createdDate ?? null,
      requestedBy: performerName(med.performerDetails),
    });
  }

  return out;
}
